import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { UpdateInvoiceInput } from '@accounting/shared';
import { useInvoice, useUpdateInvoice } from '../../hooks/useInvoices';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { InvoiceForm, type InvoiceFormValues } from '../../components/invoices/InvoiceForm';
import { ApiError } from '../../lib/api';
import { formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/invoices/$id_/edit')({
  component: EditInvoicePage,
});

function EditInvoicePage() {
  const { id } = Route.useParams();
  const companyId = useActiveCompanyId();
  const navigate = useNavigate();
  const { data: invoice, isLoading, error } = useInvoice(companyId, id);
  const update = useUpdateInvoice(companyId);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }
  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !invoice) return <p className="field-error">Failed to load invoice.</p>;

  if (invoice.status === 'void') {
    return (
      <div>
        <Link to="/invoices/$id" params={{ id }} style={{ fontSize: 13 }}>
          ← Invoice {invoice.invoiceNumber}
        </Link>
        <div className="card" style={{ padding: 20, marginTop: 8 }}>
          <p style={{ marginTop: 0 }}>This invoice has been voided and can no longer be edited.</p>
        </div>
      </div>
    );
  }
  const paid = parseFloat(invoice.amountPaid);

  const initial: InvoiceFormValues = {
    customerId: invoice.customerId ?? '',
    invoiceDate: invoice.invoiceDate,
    dueDate: invoice.dueDate,
    terms: invoice.terms ?? '',
    memo: invoice.memo ?? '',
    lines: invoice.lines.map((l) => ({
      accountId: l.accountId,
      description: l.description ?? '',
      quantity: l.quantity,
      unitPrice: l.unitPrice,
    })),
  };

  const errorMessage =
    update.error instanceof ApiError
      ? (update.error.body as { error?: string } | null)?.error ?? 'Could not update invoice'
      : update.error?.message;

  return (
    <div>
      <Link to="/invoices/$id" params={{ id }} style={{ fontSize: 13 }}>
        ← Invoice {invoice.invoiceNumber}
      </Link>
      <h1 style={{ fontSize: 24, margin: '8px 0 16px' }}>Edit invoice {invoice.invoiceNumber}</h1>

      {paid > 0 && (
        <div className="card" style={{ padding: 12, marginBottom: 12, fontSize: 13 }}>
          This invoice has <strong>{formatCurrency(invoice.amountPaid)}</strong> in payments. They stay
          attached — the balance due recomputes after you save. The new total can’t be below what’s
          already paid.
        </div>
      )}

      <InvoiceForm
        companyId={companyId}
        initial={initial}
        submitLabel="Save changes"
        submitting={update.isPending}
        errorMessage={errorMessage}
        cancelTo="/invoices"
        onSubmit={(input) =>
          update.mutate(
            { id, input: input as UpdateInvoiceInput },
            { onSuccess: () => void navigate({ to: '/invoices/$id', params: { id } }) },
          )
        }
      />
    </div>
  );
}
